import { useQueryClient } from "@tanstack/react-query";
import { $baseApi, LENDING_CSRF_QUERY_KEY } from "@lending/lib/api";

/** openapi-react-query key prefix for the session-state probe. */
const SESSION_QUERY_KEY = ["get", "/v2/lending/session"] as const;

/**
 * Reports whether the visitor holds a valid lending session. The session cookie
 * is httponly, so this probe is the client's only signal of standalone auth state.
 *
 * Cached for the life of the page: auth state only changes at the few mutations
 * that mint the session cookie (application create, portal login verify) — those
 * call `useClearSessionStateCache` — and at logout, which navigates away.
 */
function useGetSessionQuery({ enabled = true }: { enabled?: boolean } = {}) {
  return $baseApi.useQuery(
    "get",
    "/v2/lending/session",
    {},
    {
      enabled,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );
}

/**
 * Clears what the client caches per session cookie, for mutations that mint one: the
 * probe answer above, and the lending CSRF token, a synchronizer token held on the Session.
 * Invalidation (stale + refetch) rather than removal, so the menu refreshes in
 * place instead of unmounting while the refetch runs.
 */
function useClearSessionStateCache() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: LENDING_CSRF_QUERY_KEY }),
    ]);
}

export { useGetSessionQuery, useClearSessionStateCache };
